/**
 * Statut d'abonnement d'un contact sur un canal, dérivé des colonnes générées
 * de `shops_contacts`. Cette logique était dupliquée dans la fiche contact et
 * les deux listings de contacts, où elle avait divergé.
 *
 * Trois règles que la base impose et qu'il faut respecter ici :
 * - `optin_data_<canal>` est forcé à 0 dès que `unsub_data_<canal>` vaut 1. Le
 *   couple (optin > 0, unsub = 1) n'existe donc jamais : le désabonnement se lit
 *   sur `unsub` seul, jamais sur une combinaison des deux.
 * - Le push n'a pas de colonne `optin_data` : la présence du token vaut
 *   consentement.
 * - La coordonnée prime sur le désabonnement. Sans email, téléphone ni token, le
 *   canal est « non renseigné » même si un désabonnement global a posé unsub = 1
 *   sur tous les canaux.
 *
 * Le rejet (`reject_data_*`) n'entre pas dans ce calcul : aucun écran ne
 * l'affiche aujourd'hui, un email en hard bounce reste présenté comme opt-in.
 */
export type ChannelSubscriptionStatus =
  | 'no_coordinate'
  | 'unsubscribed'
  | 'subscribed'
  | 'double_optin'
  | 'simple_optin'
  | 'not_subscribed';

export type SubscriptionChannel = 'email' | 'sms' | 'push';

const toNumber = (value: number | string | null | undefined): number => {
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return value ?? 0;
};

/**
 * `optin` est ignoré pour le canal push, qui n'en a pas.
 */
const getChannelSubscriptionStatus = (
  channel: SubscriptionChannel,
  hasCoordinate: boolean,
  optin: number | string | null | undefined,
  unsub: number | string | null | undefined,
): ChannelSubscriptionStatus => {
  if (!hasCoordinate) return 'no_coordinate';
  if (toNumber(unsub) > 0) return 'unsubscribed';
  if (channel === 'push') return 'subscribed';

  const optinValue = toNumber(optin);
  if (optinValue >= 2) return 'double_optin';
  if (optinValue === 1) return 'simple_optin';
  return 'not_subscribed';
};

export default getChannelSubscriptionStatus;
